import { Routes } from '@angular/router';
import { authGuard, UserRole } from '@ess-front/shared';
import { UserTranslationResolver } from '@app/utils/resolvers/user-translation.resolver';
import { TripTranslationResolver } from '@app/utils/resolvers/trip-translation.resolver';
import { DefaultTranslationResolver } from '@app/utils/resolvers/default-translation.resolver';
import { ParamTranslationResolver } from '@app/utils/resolvers/param-translation-resolver.service';
import { UserHashTranslationResolver } from '@app/utils/resolvers/user-hash-translation.resolver';
import { LoginGuard } from '@app/utils/guards/login.guard';
import { brandGuard } from '@app/utils/guards/brand.guard';
import { DialogType } from '@app/utils/models/dialog-type.model';
import { Domain } from '@app/utils/models/domain.model';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'members-area',
    pathMatch: 'full',
  },
  {
    path: 'members-area/login',
    redirectTo: 'login',
    pathMatch: 'full',
  },
  {
    path: 'visaap',
    pathMatch: 'full',
    loadComponent: () => import('@app/apply/ui/apply.component').then(x => x.ApplyComponent),
    data: {
      brand: 'visaap',
    },
  },
  {
    path: 'apply/:brand',
    pathMatch: 'full',
    canActivate: [brandGuard],
    loadComponent: () => import('@app/apply/ui/apply.component').then(x => x.ApplyComponent),
  },
  {
    path: 'apply/:brand/:lang',
    canActivate: [brandGuard],
    loadComponent: () => import('@app/apply/ui/apply.component').then(x => x.ApplyComponent),
    resolve: {
      language: ParamTranslationResolver,
    },
  },
  {
    path: 'apply',
    loadComponent: () => import('@app/apply/ui/apply.component').then(x => x.ApplyComponent),
  },
  {
    path: 'magic-link',
    canActivate: [LoginGuard],
    resolve: {
      language: DefaultTranslationResolver,
    },
    loadComponent: () => import('@app/auth/ui/magic-link/magic-link.component').then(x => x.MagicLinkComponent),
  },
  {
    path: 'payments/:paymentResult',
    loadComponent: () =>
      import('@app/payments/ui/payments-message/payments-messages.component').then(m => m.PaymentsMessagesComponent),
    resolve: {
      language: DefaultTranslationResolver,
    },
  },
  {
    path: 'preferences-form/:hash',
    loadComponent: () =>
      import('@app/forms/ui/preferences-form/preferences-form.component').then(x => x.PreferencesFormComponent),
    resolve: {
      language: UserHashTranslationResolver,
    },
  },
  {
    path: 'preferences-form-completed',
    loadComponent: () =>
      import('@app/forms/ui/preferences-form-completed/preferences-form-completed.component').then(
        x => x.PreferencesFormCompletedComponent,
      ),
  },
  {
    path: 'trip/:hash/confirmation/:id',
    loadChildren: () => import('@app/confirmation/confirmation.module').then(m => m.ConfirmationModule),
    data: {
      type: 'bookingexperience',
    },
    resolve: {
      language: TripTranslationResolver,
    },
  },
  // EXPERIENCE - DEEPLINK - REDIRECTS
  {
    path: 'hotels/:slug',
    redirectTo: 'hotels/:slug/en',
    pathMatch: 'full',
  },
  {
    path: 'experiences/:slug',
    redirectTo: 'experiences/:slug/en',
    pathMatch: 'full',
  },
  {
    path: 'restaurants/:slug',
    redirectTo: 'restaurants/:slug/en',
    pathMatch: 'full',
  },
  {
    path: 'places/:slug',
    redirectTo: 'places/:slug/en',
    pathMatch: 'full',
  },
  // EXPERIENCE - DEEPLINK
  {
    path: 'hotels/:slug/:lang',
    loadComponent: () => import('@app/experience/ui/experience/experience.component').then(m => m.ExperienceComponent),
    data: {
      endpoint: 'hotel',
    },
    resolve: {
      language: ParamTranslationResolver,
    },
  },
  {
    path: 'experiences/:slug/:lang',
    loadComponent: () => import('@app/experience/ui/experience/experience.component').then(m => m.ExperienceComponent),
    data: {
      endpoint: 'experience',
    },
    resolve: {
      language: ParamTranslationResolver,
    },
  },
  {
    path: 'restaurants/:slug/:lang',
    loadComponent: () => import('@app/experience/ui/experience/experience.component').then(m => m.ExperienceComponent),
    data: {
      endpoint: 'restaurant',
    },
    resolve: {
      language: ParamTranslationResolver,
    },
  },
  {
    path: 'places/:slug/:lang',
    loadComponent: () => import('@app/experience/ui/experience/experience.component').then(m => m.ExperienceComponent),
    data: {
      endpoint: 'place',
    },
    resolve: {
      language: ParamTranslationResolver,
    },
  },
  //BOOKING EXPERIENCE
  {
    path: 'trip/:hash/experience/:id',
    loadComponent: () => import('@app/experience/ui/experience/experience.component').then(m => m.ExperienceComponent),
    data: {
      endpoint: 'bookingexperience',
    },
    resolve: {
      language: TripTranslationResolver,
    },
  },
  // NEW ITINERARY
  {
    path: 'itinerary/:hash',
    loadComponent: () => import('@app/itinerary/ui/itinerary/itinerary.component').then(m => m.ItineraryComponent),
    resolve: {
      language: TripTranslationResolver,
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@app/itinerary-timeline/ui/itinerary-timeline/itinerary-timeline.component').then(
            m => m.ItineraryTimelineComponent,
          ),
      },
      {
        path: 'chat',
        loadComponent: () =>
          import('@app/itinerary-timeline/ui/itinerary-timeline/itinerary-timeline.component').then(
            m => m.ItineraryTimelineComponent,
          ),
      },
    ],
  },
  {
    path: 'trip/:hash',
    loadComponent: () => import('@app/itinerary/ui/itinerary/itinerary.component').then(m => m.ItineraryComponent),
    resolve: {
      language: TripTranslationResolver,
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@app/itinerary-timeline/ui/itinerary-timeline/itinerary-timeline.component').then(
            m => m.ItineraryTimelineComponent,
          ),
      },
      {
        path: 'chat',
        loadComponent: () =>
          import('@app/itinerary-timeline/ui/itinerary-timeline/itinerary-timeline.component').then(
            m => m.ItineraryTimelineComponent,
          ),
      },
    ],
  },
  {
    path: 'sample-trip/:hash',
    loadComponent: () => import('@app/itinerary/ui/itinerary/itinerary.component').then(m => m.ItineraryComponent),
    resolve: {
      language: TripTranslationResolver,
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@app/itinerary-timeline/ui/itinerary-timeline/itinerary-timeline.component').then(
            m => m.ItineraryTimelineComponent,
          ),
      },
      {
        path: 'chat',
        loadComponent: () =>
          import('@app/itinerary-timeline/ui/itinerary-timeline/itinerary-timeline.component').then(
            m => m.ItineraryTimelineComponent,
          ),
      },
    ],
  },
  { path: 'trip-itinerary/:hash', redirectTo: 'itinerary/:hash', pathMatch: 'full' },
  { path: 'proposal/:hash', redirectTo: 'itinerary/:hash', pathMatch: 'full' },

  {
    path: 'itinerary/:hash/checkout',
    loadComponent: () => import('@app/itinerary/ui/checkout/checkout.component').then(c => c.CheckoutComponent),
    resolve: {
      language: TripTranslationResolver,
    },
  },
  {
    path: 'survey/:hash',
    loadChildren: () =>
      import('@app/forms/ui/satisfaction-survey/satisfaction-survey.module').then(m => m.SatisfactionSurveyModule),
    resolve: {
      language: TripTranslationResolver,
    },
  },
  {
    path: 'forbidden',
    loadComponent: () => import('@app/auth/ui/forbidden/forbidden.component').then(x => x.ForbiddenComponent),
  },
  {
    path: 'members-area',
    canActivate: [authGuard(UserRole.MEMBER, 'login')],
    loadComponent: () =>
      import('@app/users-area/ui/members-area/members-area.component').then(m => m.MembersAreaComponent),
    resolve: {
      language: UserTranslationResolver,
    },
    children: [
      {
        path: 'tl/:topLevelTripHash',
        loadComponent: () =>
          import('@app/users-area/ui/dialog-handler/dialog-handler.component').then(mod => mod.DialogHandlerComponent),
        data: { dialogType: DialogType.TopLevelTrip, domain: Domain.MEMBERS_AREA },
      },
      {
        path: 'request-trip',
        loadComponent: () =>
          import('@app/users-area/ui/dialog-handler/dialog-handler.component').then(mod => mod.DialogHandlerComponent),
        data: { dialogType: DialogType.RequestTrip, domain: Domain.MEMBERS_AREA },
      },
      {
        path: 'schedule-a-call',
        loadComponent: () =>
          import('@app/users-area/ui/dialog-handler/dialog-handler.component').then(mod => mod.DialogHandlerComponent),
        data: { dialogType: DialogType.ScheduleCall, domain: Domain.MEMBERS_AREA },
      },
    ],
  },
  {
    path: 'staff-area',
    canActivate: [authGuard(UserRole.STAFF, 'login')],
    loadComponent: () => import('@app/users-area/ui/staff-area/staff-area.component').then(m => m.StaffAreaComponent),
    resolve: {
      language: UserTranslationResolver,
    },
    title: 'Essentialist - Staff Area',
    children: [
      {
        path: 'tl/:topLevelTripHash',
        loadComponent: () =>
          import('@app/users-area/ui/dialog-handler/dialog-handler.component').then(mod => mod.DialogHandlerComponent),
        data: { dialogType: DialogType.TopLevelTrip, domain: Domain.STAFF_AREA },
      },
    ],
  },
  {
    path: 'login',
    canActivate: [LoginGuard],
    loadComponent: () => import('@app/auth/ui/login/login.component').then(x => x.LoginComponent),
    resolve: {
      language: DefaultTranslationResolver,
    },
  },
  {
    path: 'reset-password/:token/:uid',
    loadComponent: () =>
      import('@app/auth/ui/reset-password/reset-password.component').then(x => x.ResetPasswordComponent),
    resolve: {
      language: DefaultTranslationResolver,
    },
  },
  {
    path: 'forgot-password',
    canActivate: [LoginGuard],
    loadComponent: () =>
      import('@app/auth/ui/forgot-password/forgot-password.component').then(x => x.ForgotPasswordComponent),
    resolve: {
      language: DefaultTranslationResolver,
    },
  },
  {
    path: 'authorization-form/:hash',
    loadComponent: () =>
      import('@app/forms/ui/authorization-form/authorization-form.component').then(x => x.AuthorizationFormComponent),
    resolve: {
      language: UserHashTranslationResolver,
    },
  },
  {
    path: '**',
    loadComponent: () => import('@app/auth/ui/not-found/not-found.component').then(x => x.NotFoundComponent),
  },
];
